import { format } from "date-fns";
import { useState } from "react";
import { DatePicker, Notification, useToaster } from "rsuite";
import { dateQueries } from "../../../config/filter/CustomFilterConfig";
import {
  CustomFilterUnion,
  CustomFilterConfig,
  CFChangeEvent,
  CFDateItemSetting,
} from "./types";
import { f } from "../../../util/f";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../select/Select";
import { ComboBox } from "../ComboBox";
import AppliedFilterItem from "../AppliedFilterItem";
import {
  PersonFilterInput,
  FirmaFilterInput,
  KursusholdFilterInput,
  FmulinjeFilterInput,
} from "../../../interfaces/schema";
import WarningIcon from "../../icons/WarningIcon";
import { isCompleteDate } from "../../formcontrols/datePickerValue";

export default function CustomFilterDateItem<
  C extends CustomFilterUnion,
>(props: {
  config: CustomFilterConfig<C>;
  filterEvent: (event: CFChangeEvent) => void;
  settings: CFDateItemSetting[] | undefined;
}) {
  // destruction!
  const { config, filterEvent, settings } = props;
  const queries = dateQueries;

  // state things
  const [selectedQuery, setSelectedQuery] = useState(queries[0]);
  // null while the typed date is unfinished (ADV-320)
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  // for all your toasting purposes
  const toaster = useToaster();

  // handler
  function dateEventHandler(date: Date | null) {
    const d = date || selectedDate;
    if (!d) return;
    const dateInput = format(d, "yyyy-MM-dd");

    // update filter settings
    const id: CFDateItemSetting["id"] = `${selectedQuery.query}-${dateInput}`;

    if (settings?.some((set) => set.id === id)) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon text-light bg-warning`}>
              <WarningIcon />
            </span>
            Filteret er allerede tilføjet.
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );
      return;
    }

    const settingsItem: CFDateItemSetting = {
      id: id,
      query: selectedQuery,
      value: dateInput,
    };

    let updatedSettings: CFDateItemSetting[];

    if (settings) {
      updatedSettings = [...settings, settingsItem];
    } else {
      updatedSettings = [settingsItem];
    }

    if (updatedSettings.length === 0) {
      filterEvent({
        filterString: "",
      });
      return;
    }

    const filterString = f<
      | PersonFilterInput
      | FirmaFilterInput
      | KursusholdFilterInput
      | FmulinjeFilterInput
    >()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            [config.queryKey]: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const filterObj:
      | PersonFilterInput
      | FirmaFilterInput
      | KursusholdFilterInput
      | FmulinjeFilterInput = {
      or: updatedSettings.map((set) => {
        return {
          [config.queryKey]: {
            [set.query.query]: set.value,
          },
        };
      }),
    };

    filterEvent({
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: updatedSettings,
    });
  }

  function removeFilterEventHandler(id: string) {
    const filteredSettings = settings?.filter((set) => set.id !== id);

    if (filteredSettings?.length === 0) {
      filterEvent({
        filterString: "",
        filterObj: {},
        filterSettings: undefined,
      });
      return;
    }

    const filterString = f<
      | PersonFilterInput
      | FirmaFilterInput
      | KursusholdFilterInput
      | FmulinjeFilterInput
    >()
      .addFilter(
        "or",
        filteredSettings?.map((set) => {
          return {
            [config.queryKey]: {
              [set.query.query]: set.value,
            },
          };
        }),
      )
      .build();

    const filterObj = {
      or: filteredSettings?.map((set) => {
        return {
          [config.queryKey]: {
            [set.query.query]: set.value,
          },
        };
      }),
    };

    filterEvent({
      filterString: filterString,
      filterObj: filterObj,
      filterSettings: filteredSettings,
    });
  }

  return (
    <div className="c-custom-filter__content">
      <div className="c-applied-filter-group">
        {settings?.map((set) => (
          <AppliedFilterItem
            labelPrefix={`${set.query.displayName}`}
            label={`${format(new Date(set.value), "dd-MM-yyyy")}`}
            removeFilterHandler={() => {
              const id: CFDateItemSetting["id"] = `${set.query.query}-${set.value}`;
              removeFilterEventHandler(id);
            }}
            key={`${set.query.query}-${set.value}`}
          />
        ))}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedQuery?.query}
          onValueChange={(value: string) => {
            const query = queries.find((q) => q.query === value);
            if (query) {
              setSelectedQuery(query);
            }
          }}
        >
          <SelectTrigger placeholder="Vælg query type" badge />
          <SelectContent small>
            {queries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedDate}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedDate(isCompleteDate(date) ? date : null);
          }}
        />
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 flex-1`}
          disabled={!selectedDate}
          onClick={() => {
            dateEventHandler(null);
          }}
        >
          OK
        </button>
      </ComboBox>
    </div>
  );
}
